import { cn } from '@/lib/cn';
import { SkillFileUploadStatus, type PendingSkillFileUpload } from '@heidi/agents';
import { SearchInput } from '@heidi/design-system/search-input';
import { TypographyCaption } from '@heidi/design-system/typography';
import { listUserDocuments } from '@heidi/evidence/data';
import { useQuery } from '@tanstack/react-query';
import { Check, File, FileOutput, Loader2, type LucideIcon } from 'lucide-react';
import { useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { libraryDocumentPickerListMessages as m } from '@heidi/agents/data';

export const WORKFLOW_CONTEXT_DOCS_QUERY_KEY = 'workflow-context-documents';
export const LIBRARY_ARTEFACT_DOCS_QUERY_KEY = 'library-artefact-documents';

export interface PickedWorkflowContextDocument {
  id: string;
  name: string;
}

export type PickedLibraryArtefactDocument = PickedWorkflowContextDocument;

type LibraryDocumentPickerKind = 'workflow_context' | 'artifact';

const PICKER_CONFIG: Record<
  LibraryDocumentPickerKind,
  {
    queryKey: string;
    documentKind: 'workflow_context' | 'artifact';
    testId: string;
    rowTestId: string;
    searchPlaceholder: keyof typeof m;
    searchAriaLabel: keyof typeof m;
    loadingLabel: keyof typeof m;
    emptySearchLabel: keyof typeof m;
    emptyLabel: keyof typeof m;
    Icon: LucideIcon;
  }
> = {
  workflow_context: {
    queryKey: WORKFLOW_CONTEXT_DOCS_QUERY_KEY,
    documentKind: 'workflow_context',
    testId: 'workflow-context-document-picker',
    rowTestId: 'workflow-context-document-picker-row',
    searchPlaceholder: 'workflowContextSearchPlaceholder',
    searchAriaLabel: 'workflowContextSearchAria',
    loadingLabel: 'workflowContextLoading',
    emptySearchLabel: 'workflowContextEmptySearch',
    emptyLabel: 'workflowContextEmpty',
    Icon: File,
  },
  artifact: {
    queryKey: LIBRARY_ARTEFACT_DOCS_QUERY_KEY,
    documentKind: 'artifact',
    testId: 'library-artefact-document-picker',
    rowTestId: 'library-artefact-document-picker-row',
    searchPlaceholder: 'artifactSearchPlaceholder',
    searchAriaLabel: 'artifactSearchAria',
    loadingLabel: 'artifactLoading',
    emptySearchLabel: 'artifactEmptySearch',
    emptyLabel: 'artifactEmpty',
    Icon: FileOutput,
  },
};

interface LibraryDocumentPickerListProps {
  kind?: LibraryDocumentPickerKind;
  selectedIds?: string[];
  pendingUpload?: PendingSkillFileUpload | null;
  onSelect: (document: PickedWorkflowContextDocument) => void;
  disabled?: boolean;
  className?: string;
}

/** Searchable list of library documents for a given `document_kind`. */
export function LibraryDocumentPickerList({
  kind = 'workflow_context',
  selectedIds = [],
  pendingUpload = null,
  onSelect,
  disabled = false,
  className,
}: LibraryDocumentPickerListProps): JSX.Element {
  const config = PICKER_CONFIG[kind];
  const intl = useIntl();
  const { data, isLoading, isFetching } = useQuery({
    queryKey: [config.queryKey, config.documentKind],
    queryFn: () => listUserDocuments('all', config.documentKind),
    staleTime: 10_000,
  });
  const [query, setQuery] = useState('');
  const Icon = config.Icon;

  const filtered = useMemo(() => {
    const documents = data?.documents ?? [];
    const q = query.trim().toLowerCase();
    if (!q) return documents;
    return documents.filter((d) => d.filename.toLowerCase().includes(q));
  }, [data?.documents, query]);

  return (
    <div className={cn('flex flex-col gap-2', className)} data-testid={config.testId}>
      <SearchInput
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        disabled={disabled || isLoading}
        placeholder={intl.formatMessage(m[config.searchPlaceholder])}
        aria-label={intl.formatMessage(m[config.searchAriaLabel])}
        clearAriaLabel={intl.formatMessage({
          defaultMessage: 'Clear search',
          id: '4YJHutR26p',
        })}
      />

      <div className="flex max-h-64 flex-col gap-1 overflow-y-auto [scrollbar-gutter:stable]">
        {pendingUpload && kind === 'workflow_context' ? (
          <SkillFileUploadStatus
            upload={pendingUpload}
            uploadProgress={pendingUpload.uploadProgress}
            variant="chip"
          />
        ) : null}
        {isLoading ? (
          <div className="flex items-center gap-2 px-3 py-4 text-text-secondary">
            <Loader2 className="size-4 animate-spin" />
            <TypographyCaption>
              {intl.formatMessage(m[config.loadingLabel])}
            </TypographyCaption>
          </div>
        ) : filtered.length === 0 ? (
          <TypographyCaption className="px-3 py-4 text-text-secondary">
            {query.trim()
              ? intl.formatMessage(m[config.emptySearchLabel])
              : intl.formatMessage(m[config.emptyLabel])}
            {isFetching && !isLoading ? (
              <>
                {' '}
                <FormattedMessage {...m.refreshing} />
              </>
            ) : null}
          </TypographyCaption>
        ) : (
          filtered.map((doc) => {
            const isSelected = selectedIds.includes(doc.document_id);
            return (
              <button
                key={doc.document_id}
                type="button"
                disabled={disabled}
                onClick={() => onSelect({ id: doc.document_id, name: doc.filename })}
                aria-pressed={isSelected}
                className={cn(
                  'flex w-full items-center gap-3 rounded-lg border px-3 py-2 text-left transition-colors',
                  'disabled:cursor-not-allowed disabled:opacity-60',
                  isSelected
                    ? 'border-border-pressed bg-background-secondary'
                    : 'border-border bg-surface hover:bg-surface-hover'
                )}
                data-testid={config.rowTestId}
              >
                <Icon className="size-4 shrink-0 text-text-secondary" />
                <span className="min-w-0 flex-1 truncate text-sm text-text-primary">
                  {doc.filename}
                </span>
                {isSelected && (
                  <Check className="size-4 shrink-0 text-text-active" aria-hidden />
                )}
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}

/** Back-compat alias for the work-output picker screen. */
export function LibraryArtefactDocumentPickerList(
  props: Omit<LibraryDocumentPickerListProps, 'kind'>
): JSX.Element {
  return <LibraryDocumentPickerList kind="artifact" {...props} />;
}
